import type {
  AxisId,
  DatapointMetadata,
  SeriesId,
  XYChartAxisInternal,
} from '../../types/xy-chart-internals.js';
import type { BarDatapoint } from '../../types/xy-chart.js';
import { getStackingData } from '../utils/stacking/stack-datapoints.js';

export interface GroupingResult {
  datapointMetadata: DatapointMetadata;
}

interface GroupData {
  totalPositive: number;
  totalNegative: number;
}

/**
 * A class that encapsulates grouping operations for series variants.
 * This class can be instantiated by any series strategy that needs grouping functionality.
 */
export class GroupableSeriesHelper {
  private static readonly STARTING_POINT = 0;
  private readonly groupData: Map<string, GroupData>;
  public readonly axisIdToDimension: Map<AxisId, Set<SeriesId>>;

  constructor() {
    this.groupData = new Map();
    this.axisIdToDimension = new Map();
  }

  /**
   * Calculate total height of group bin.
   * Should be called at the beginning of a new grouping calculation cycle (preprocess phase).
   */
  processGroupDomain(
    datapoint: BarDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ): void {
    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
    );

    if (typeof value === 'number') {
      const acc = this.groupData.get(stackKey) ?? {
        totalPositive: GroupableSeriesHelper.STARTING_POINT,
        totalNegative: GroupableSeriesHelper.STARTING_POINT,
      };
      if (value >= 0) {
        acc.totalPositive += value;
      } else {
        acc.totalNegative += Math.abs(value);
      }
      this.groupData.set(stackKey, acc);
    }
  }

  /**
   * Returns the total bin domain for a given group key.
   * The bin domain is the total height of the group, including both positive and negative values.
   * Note: totalNegative is already stored as an absolute value.
   */
  getGroupBinDomain(groupKey: string): number | undefined {
    const groupDomainObj = this.groupData.get(groupKey);
    if (!groupDomainObj) {
      return undefined;
    }
    return groupDomainObj.totalPositive + groupDomainObj.totalNegative;
  }

  /**
   * Processes a datapoint for grouping, calculating the bin domain metadata.
   * Also registers the series in the dimension map for the primary axis.
   * Returns metadata with binDomain set but no accumulator (bars remain side-by-side).
   */
  processGroupDatapoint(
    datapoint: BarDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: string,
    datapointMetadata: DatapointMetadata,
  ): GroupingResult {
    const primaryAxis = chartOrientation === 'vertical' ? xAxis : yAxis;
    const dimension = this.axisIdToDimension.get(primaryAxis.id);

    if (dimension) {
      dimension.add(seriesId);
      this.axisIdToDimension.set(primaryAxis.id, dimension);
    } else {
      this.axisIdToDimension.set(
        primaryAxis.id,
        new Set<string>().add(seriesId),
      );
    }

    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
    );

    if (typeof value !== 'number') {
      return { datapointMetadata };
    }

    const binDomain = this.getGroupBinDomain(stackKey);

    return {
      datapointMetadata: {
        ...datapointMetadata,
        binDomain,
      },
    };
  }
}
